import { Theme } from '@mui/material/styles';

/**
 * カードのホバー演出（ゲーム／アニメ／映画で共通）。
 *
 * **1箇所にまとめること。** 同じスタイルを各カードに複製すると、
 * 直すときに片方だけ直り、増えるときに片方だけ増える
 * （`commonPaperStyle` が10ファイルに複製されて同じバグを10箇所に抱えていた前例がある）。
 *
 * 【安く保つための条件】
 * グリッドには数十枚並ぶので、**アイドル時のコストをゼロに保つ**のが絶対条件。
 * - `will-change` を常時付けない。50枚ぶんレイヤーが昇格して GPU メモリを食う
 * - 動かすのは transform と box-shadow だけ
 * ホバーは同時に1枚しか起きないので、1枚あたりのコストは気にしなくてよい。
 *
 * 枠線に `primary.main` を使うので、プロフィール配下では**所有者のアクセント色**になり、
 * 他ページではブランド色に戻る（入れ子の ThemeProvider が primary を差し替えているため）。
 * 設定を増やさずにテーマが効く場所が増える。
 */

/**
 * 枠線を描く層。**画像より前面に置くこと。**
 * CardMedia に inset の影を掛けても見えない。inset は要素自身の背景レイヤーに
 * 描かれるので、その上に敷いた画像レイヤーが完全に覆ってしまう。
 */
export const CARD_EDGE_CLASS = 'card-edge';
/** ホバーで拡大する層に付ける。カバー画像だけに付けること（バッジまで拡大しないため） */
export const CARD_ZOOM_CLASS = 'card-zoom';
/** 光沢の帯に付ける */
export const CARD_SHEEN_CLASS = 'card-sheen';

/**
 * 枠線と光沢を有効にする範囲。配下でだけ演出が全部乗り、外は拡大のみ。
 * prop ではなく class なのは、カードが 20 箇所以上から呼ばれていて
 * 中継点すべてに prop が生えるため。付与位置: `pages/ProfilePage.tsx`
 */
export const CARD_FX_SCOPE_CLASS = 'card-fx-scope';

export const cardHoverSx = (theme: Theme) => ({
  // **反応はカード全体で受けるが、見た目が変わるのはカバー画像だけ。**
  // タイトル行まで枠で囲むと、文字の周りに箱ができて野暮ったくなる。
  // カードの視覚的な実体はカバー画像なので、演出はそこに閉じる。
  [`& .${CARD_ZOOM_CLASS}`]: {
    transition: 'transform 0.45s cubic-bezier(0.2, 0.7, 0.3, 1)',
  },
  // **theme.ts の MuiCard が全カードに translateY(-5px) と影を掛けている。**
  // 持ち上げは枠線ともズームとも喧嘩する（カードごと動くと枠が動き、中身の動きが読めない）ので、
  // ここで必ず打ち消す。ポインタ判定の外に置いてあるのは、タッチ端末で :hover が
  // 発火したときにテーマ側だけが残るのを防ぐため。
  '&:hover': { transform: 'none', boxShadow: 'none' },
  // **ホバーはポインタの種別で判定する。画面幅で分けないこと。**
  // 幅で分けるとタッチ対応PCやタブレットでホバー状態が張り付いたままになる。
  '@media (hover: hover) and (pointer: fine)': {
    // 拡大はどこでも。枠線と光沢はスコープ配下だけ(一覧で 50 枚光ると落ち着かない)
    [`&:hover .${CARD_ZOOM_CLASS}`]: { transform: 'scale(1.03)' },
    [`.${CARD_FX_SCOPE_CLASS} &:hover .${CARD_EDGE_CLASS}`]: {
      // inset にするのは、外向きの影が親の overflow:hidden で切り落とされるため。
      // inset なら画像の角丸にも正確に沿う。
      boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    },
    [`.${CARD_FX_SCOPE_CLASS} &:hover .${CARD_SHEEN_CLASS}`]: {
      transform: 'translateX(260%) skewX(-18deg)',
      transition: 'transform 0.7s ease',
    },
  },
});

/**
 * 光沢の帯。カード内に1つ置く（カードが overflow:hidden である前提）。
 *
 * 帯は細く、白は薄く。太く濃くすると一気に安っぽくなる。
 * ここで `transition: none` にしてあるのは**戻りをアニメーションさせない**ため。
 * 往復させると「行って帰ってくる」動きになって玩具っぽく見える。
 * ホバー側だけが transition を持つので、離した瞬間に無音で初期位置へ戻る。
 */
export const CARD_EDGE_SX = {
  position: 'absolute',
  inset: 0,
  // 画像・光沢より前面。DOM の順序ではなく z-index で決めるので、
  // カード側の子要素をどこに足しても影響を受けない。
  zIndex: 2,
  pointerEvents: 'none',
  borderRadius: 'inherit',
  boxShadow: 'inset 0 0 0 0 transparent',
  transition: 'box-shadow 0.25s ease',
} as const;

export const CARD_SHEEN_SX = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: '-60%',
  width: '40%',
  zIndex: 1,
  pointerEvents: 'none',
  transform: 'translateX(0) skewX(-18deg)',
  transition: 'none',
  background:
    'linear-gradient(90deg, transparent, rgba(255,255,255,0.10) 50%, transparent)',
} as const;
